import { useEffect, useRef, type ReactNode } from 'react';
import { useReducer, useSpacetimeDB } from 'spacetimedb/react';
import { reducers } from '../module_bindings';
import { BannedPage } from './BannedPage';
import { ConnectionStatusScreen } from './ConnectionStatusScreen';
import { DB_NAME, HOST } from './config';
import { setActive, upsertIdentity } from './identityStore';
import {
  notifyIdentityStoreUpdate,
  setActiveTokenHash,
  setSessionState,
  useCurrentUser,
  useSessionState,
} from './hooks';

/**
 * Lives inside the SpacetimeDBProvider. Responsible for:
 *
 * - Driving the session FSM forward as connection + user-row state changes:
 *     CONNECTING → (auto enterWorld if no User) → PLAYING (or BANNED).
 *   There is no user-facing "Enter the canopy" intermediate screen — the
 *   landing page or the picker's Create-new were already that click.
 * - Persisting the local identity ONLY when the user reaches PLAYING. For
 *   Create-new flows the local identity is created here for the first
 *   time; for returning users the existing entry's `lastKnown*` fields
 *   are refreshed. Pre-PLAYING is intentionally NOT persisted, so closing
 *   the tab during connect/enter doesn't leave a half-identity behind.
 * - Rendering BannedPage when BANNED, children otherwise.
 *
 * Defensive: only steers the FSM when sessionState is already in a
 * connect-state, so an explicit transition from the in-game menu (e.g.
 * IDENTITY_SELECT) isn't clobbered by a stale render of this effect
 * before the parent unmounts us.
 */
export function ConnectedShell({
  children,
  connectedToken,
  requestReconnect,
}: {
  children: ReactNode;
  connectedToken: string | undefined;
  requestReconnect: () => void;
}) {
  const conn = useSpacetimeDB();
  const sessionState = useSessionState();
  const me = useCurrentUser();
  const enterWorld = useReducer(reducers.enterWorld);

  // Reconnect-on-resume. Mobile sleep kills the WebSocket; the SDK does
  // not auto-reconnect. We listen for the standard wake/resume signals
  // and ask the parent to remount the provider when the connection is
  // dead. `wasActive` keeps us from spuriously reconnecting during the
  // initial connect (where isActive starts false legitimately).
  const wasActive = useRef(false);
  useEffect(() => {
    if (conn?.isActive) wasActive.current = true;
  }, [conn?.isActive]);

  useEffect(() => {
    const checkAndReconnect = () => {
      if (!conn?.isActive && wasActive.current) requestReconnect();
    };
    const onVisibility = () => {
      if (!document.hidden) checkAndReconnect();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      // Safari bfcache restore — page resumed from memory after sleep.
      if (e.persisted) checkAndReconnect();
    };
    const onOnline = () => {
      // Network just came back; the socket is almost certainly dead.
      // Skip the wasActive guard — even the initial connect benefits
      // from a retry kick when network restores.
      requestReconnect();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', checkAndReconnect);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', checkAndReconnect);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [conn, requestReconnect]);

  // Track what we last persisted so we re-persist on real changes (name
  // reroll, admin promotion) but skip writes on every harmless rerender.
  const lastPersisted = useRef<{ name: string | undefined; isAdmin: boolean } | null>(
    null
  );
  // Single-shot guard so we only call enterWorld once per connection,
  // even if React re-runs the effect (e.g. StrictMode dev double-invoke).
  // useRef preserves its `.current` across StrictMode's simulated
  // unmount/remount within the same component instance — exactly what
  // we want — and resets to its initial value when the component truly
  // remounts (e.g. after Switch identity / Log out re-keys the parent).
  // DO NOT add a `useEffect(() => { triedEnter.current = false; }, [])`
  // reset here — it would re-fire enterWorld in StrictMode and the
  // second call would error with "user already exists".
  const triedEnter = useRef(false);

  useEffect(() => {
    const inConnectState =
      sessionState === 'CONNECTING' ||
      sessionState === 'CONNECTED_NO_USER' ||
      sessionState === 'PLAYING' ||
      sessionState === 'BANNED';
    if (!inConnectState) return;

    if (!conn?.isActive) return;

    // Tables haven't loaded yet — wait. Don't fire enterWorld blindly,
    // or returning identities would get duplicate User rows.
    if (me === undefined) return;

    if (me === null) {
      // Connected, tables loaded, no User row → auto-enter the world.
      // Covers fresh-anon (0-identity first visit, picker Create-new) and
      // wipe-recovery (saved identity whose User row is gone).
      if (sessionState !== 'CONNECTED_NO_USER') {
        setSessionState('CONNECTED_NO_USER');
      }
      if (!triedEnter.current) {
        triedEnter.current = true;
        enterWorld();
      }
      return;
    }

    if (me.isBanned) {
      if (sessionState !== 'BANNED') setSessionState('BANNED');
      return;
    }

    if (sessionState !== 'PLAYING') setSessionState('PLAYING');

    // Persist the identity to localStorage now that the User row exists.
    // For Create-new flows this is the first save; for returning users
    // it refreshes lastKnown*. Skip the write if nothing changed.
    if (!connectedToken) return;
    const last = lastPersisted.current;
    if (last && last.name === me.name && last.isAdmin === me.isAdmin) return;

    void (async () => {
      const stored = await upsertIdentity(HOST, DB_NAME, connectedToken, {
        lastKnownUserName: me.name,
        lastKnownIsAdmin: me.isAdmin,
      });
      setActive(HOST, DB_NAME, stored.tokenHash);
      setActiveTokenHash(stored.tokenHash);
      notifyIdentityStoreUpdate();
      lastPersisted.current = { name: me.name, isAdmin: me.isAdmin };
    })();
  }, [conn?.isActive, me, sessionState, connectedToken, enterWorld]);

  if (!conn?.isActive) {
    return <ConnectionStatusScreen label="Connecting…" />;
  }
  if (sessionState === 'BANNED') {
    return <BannedPage />;
  }
  // For CONNECTED_NO_USER, render children (the canopy) and the brief
  // "Entering…" indicator over it. Once the User+Character appear, the
  // indicator disappears automatically.
  if (sessionState === 'CONNECTED_NO_USER' || me === undefined) {
    return (
      <>
        {children}
        <Overlay label="Entering the canopy…" />
      </>
    );
  }
  return <>{children}</>;
}

function Overlay({ label }: { label: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0, 0, 0, 0.55)',
        color: '#e7d9b6',
        padding: '10px 18px',
        borderRadius: 6,
        fontFamily: 'system-ui, sans-serif',
        fontSize: 13,
        zIndex: 25,
        pointerEvents: 'none',
      }}
    >
      {label}
    </div>
  );
}
